/**
 * Rapprochement d'un paiement reçu avec les factures qu'il règle.
 *
 * Ce module ne parle à aucune base de données. Il prend des paiements lus par
 * `parseCoda`, un index de factures, et rend un **plan** : ce qu'il propose
 * d'affecter, et ce qu'il laisse à la file manuelle avec la raison.
 *
 * Cette séparation n'est pas de la coquetterie. Elle permet de faire tourner le
 * moteur sur des relevés réels et de regarder le résultat **avant** d'écrire
 * quoi que ce soit en base — ce qui est le seul moyen honnête de savoir si un
 * rapprochement automatique est digne de confiance.
 *
 * ## Ce que les données réelles ont dicté
 *
 * Mesuré sur 22 fichiers CODA, du 8 juin au 21 juillet 2026, 719 paiements
 * reçus :
 *
 * - **21,4 %** portent une référence `CDV-<TAG>-<AASEQ>`, et les 153 références
 *   distinctes lues correspondent toutes à une facture réelle. C'est l'échelon
 *   qui produit des résultats aujourd'hui.
 * - **71,1 %** portent une communication structurée appartenant à un autre
 *   système — garderies, parascolaire, études dirigées — qui arrive sur le même
 *   compte. Ce n'est pas un cas marginal, c'est le cas majoritaire.
 * - **7,5 %** ne portent aucune référence, mais pèsent près de la moitié des
 *   montants : ce sont les subsides et les virements d'organisations. La file
 *   manuelle porte donc l'essentiel de l'argent.
 *
 * Sur les 152 factures ainsi référencées : **127 sont payées au centime**, 25
 * sont sous-payées (1 729,80 € restant dus au total), et **aucune n'est
 * sur-payée**. Deux factures ont été réglées en deux versements.
 */

import type { CodaMovement } from './coda';
import { PAYMENT_STRUCTURED_TYPES, TAGS_EXCLUS, normalizeReference } from './communication';
import { avantLaFacturation, classifyOutOfScope, type OutOfScopeMatch } from './outOfScope';

/** Pourquoi un paiement, ou une part de paiement, part en file manuelle. */
export type ManualReason =
  /** Aucune référence exploitable dans la communication. */
  | 'no_reference'
  /** Une référence a été lue, mais aucune facture ne lui correspond. */
  | 'unknown_reference'
  /** Communication structurée valide, mais inconnue de nos factures : un autre système. */
  | 'foreign_communication'
  /** La facture citée est annulée. Le paiement demande une décision humaine. */
  | 'invoice_cancelled'
  /** La facture citée est déjà entièrement couverte. */
  | 'invoice_settled'
  /**
   * La facture citée est entièrement annulée par des notes de crédit.
   *
   * Distinguée de `invoice_settled` : rien n'est dû, mais rien n'a été payé
   * non plus. Le paiement reçu concerne donc autre chose, ou doit repartir —
   * et dire « déjà réglée » enverrait chercher un paiement qui n'existe pas.
   */
  | 'invoice_credited'
  /** Une part du paiement dépasse ce que la facture attendait encore. */
  | 'surplus'
  /**
   * La communication a la forme d'une activité tenue hors de cette
   * comptabilité : facturation partenaires, parascolaire, études dirigées.
   * Proposé, jamais applique : classer hors perimetre reste une decision
   * comptable.
   */
  | 'out_of_scope_pattern';

/** Une facture telle que le moteur a besoin de la connaître. */
export interface InvoiceRef {
  id: string;
  invoiceNumber: string;
  amountCents: number;
  /** Déjà couvert par des allocations existantes. */
  allocatedCents: number;
  /**
   * Total des notes de crédit rattachées à cette facture.
   *
   * Se déduit du montant : une facture de 110,00 € portant une note de crédit
   * de 55,00 € ne réclame plus que 55,00 €. Mesuré en production le
   * 3 septembre 2026 : 655 factures concernées, 52 543,45 € au total, et sur
   * les factures déjà rapprochées les familles ont payé **exactement le net**.
   *
   * Ignorer cette colonne, c'est proposer d'affecter plus que ce qui est dû, et
   * se faire refuser par l'invariant 2 de la base.
   */
  creditNotesCents?: number;
  status: string;
  /** Les 12 chiffres de sa communication structurée, quand elle en a une. */
  structuredDigits?: string | null;
}

/**
 * Une référence de facture citée dans une communication.
 *
 * `tolerated` distingue la forme complète `CDV-<TAG>-<AASEQ>`, sûre par
 * construction, des formes rattrapées où le préfixe manque ou est fautif. Une
 * référence tolérée n'est acceptée que si le tag cité est celui de la facture
 * trouvée par les sept chiffres.
 */
export interface LegacyRef {
  digits: string;
  tag: string;
  tolerated: boolean;
  /**
   * Le numéro ENTIER, canonisé, quand la référence est au format historique
   * `CDV-AAMMJJ-NNNNN`.
   *
   * Il existe parce que les sept chiffres ne suffisent pas à retrouver ces
   * factures-là. Les recherches en base cherchent `digits` DANS le numéro brut
   * — `invoice_number.ilike.*3000010*` — et `CDV-250530-00010` ne contient pas
   * `3000010` : le tiret est au milieu. Sans le numéro entier, la référence se
   * lit et ne trouve rien.
   */
  numeroComplet?: string;
}

export interface ProposedAllocation {
  invoiceId: string;
  invoiceNumber: string;
  amountCents: number;
  method: 'structured' | 'legacy_reference';
}

export interface MatchPlan {
  /** La référence bancaire du mouvement : clé d'idempotence de l'import. */
  externalRef: string;
  amountCents: number;
  valueDate: string;
  allocations: ProposedAllocation[];
  /** Ce qui reste non affecté après les allocations proposées. */
  leftoverCents: number;
  /** Vide quand tout le paiement a trouvé sa place. */
  manualReasons: ManualReason[];
  /** Catégorie hors périmètre reconnue à la forme, quand il y en a une. */
  outOfScope?: OutOfScopeMatch;
  /**
   * La part du paiement à écarter, quand une forme hors périmètre est reconnue.
   *
   * Vaut le montant entier pour un paiement qui ne cite que du hors périmètre,
   * et **le seul reste** quand la communication cite AUSSI une facture d'ici.
   * Un virement qui porte `CDV-MC-2601234 + PSD-25` paie la facture à hauteur
   * de ce qu'elle réclame, et le solde s'en va vers l'autre comptabilité :
   * couper est plus juste que de tout attribuer à l'un des deux.
   */
  outOfScopeCents?: number;
}

/**
 * Index de recherche des factures.
 *
 * La recherche par format historique se fait sur les **7 chiffres finaux** du
 * numéro, jamais sur le numéro entier. Le tag (`WBK`, `ISA`, `MC`, `NDSC`) ne
 * porte aucune information de numérotation : la séquence est annuelle et
 * globale depuis juin 2025. Vérifié en production — sur 3 511 factures, les 7
 * chiffres sont uniques, zéro collision. Chercher sur le numéro complet
 * échouerait dès qu'un parent écrit le mauvais tag.
 */
export interface InvoiceIndex {
  byStructured(digits: string): InvoiceRef | undefined;
  byLegacyDigits(digits: string): InvoiceRef | undefined;
  /** Par le numéro entier normalisé : la seule voie sûre pour l'ancien format. */
  byLegacyNumber(numero: string): InvoiceRef | undefined;
}

/** Extrait les 7 chiffres finaux d'un numéro ou d'une référence de facture. */
export function invoiceDigits(reference: string): string | undefined {
  const m = normalizeReference(reference).match(/(\d{7})$/);
  return m ? m[1] : undefined;
}

export function buildInvoiceIndex(invoices: InvoiceRef[]): InvoiceIndex {
  const parStructure = new Map<string, InvoiceRef>();
  const parChiffres = new Map<string, InvoiceRef>();
  const parNumero = new Map<string, InvoiceRef>();

  for (const f of invoices) {
    if (f.structuredDigits) parStructure.set(f.structuredDigits, f);
    const d = invoiceDigits(f.invoiceNumber);
    if (d) parChiffres.set(d, f);
    parNumero.set(normalizeReference(f.invoiceNumber), f);
  }

  return {
    byStructured: (digits) => parStructure.get(digits),
    byLegacyDigits: (digits) => parChiffres.get(digits),
    byLegacyNumber: (numero) => parNumero.get(normalizeReference(numero)),
  };
}

/**
 * Toutes les références historiques citées dans une communication, et non
 * seulement la première.
 *
 * Aucun cas de communication citant deux factures n'apparaît dans les relevés
 * de juin-juillet. Mais le scénario est explicitement au programme — un parent
 * qui inscrit un enfant à deux semaines d'intervalle et paie les deux d'un seul
 * virement — et il ne peut pas être empêché. Autant le lire dès maintenant.
 */
export function allLegacyRefs(raw: string): LegacyRef[] {
  const refs: LegacyRef[] = [];
  const vus = new Set<string>();

  // Passe 0 : le format des TOUTES PREMIERES factures, `CDV-AAMMJJ-NNNNN`.
  //
  // 178 factures le portent, émises entre le 30 mai et le 2 juin 2025 — quatre
  // jours, quatre dates différentes, donc aucune date ne se code en dur ici.
  // Le format a été remplacé aussitôt : la population est CLOSE et ne grandira
  // plus.
  //
  // Elle échappait aux deux passes suivantes, et pour une raison de forme :
  // l'une exige des lettres après `CDV`, l'autre exige sept chiffres après le
  // tag, et il y en a six. Un virement disant
  // `CDV-250530-00010 remboursement double paiement` n'était donc relié à rien,
  // et se retrouvait classé « dépense de fonctionnement » — à écarter d'un
  // paquet, alors que c'est de l'argent de famille.
  //
  // `(?!\d)` n'est pas une précaution de style. `CDV-2506002-00072` existe en
  // production — une faute de frappe à douze chiffres. Sans cette garde, la
  // règle y lirait `CDV-250600-20007`, un numéro qui n'existe pas, et
  // rattacherait un virement à la mauvaise famille. Les deux groupes étant de
  // largeur fixe, le moteur ne peut pas revenir en arrière : il ne trouve rien,
  // ce qui est la bonne réponse.
  for (const m of normalizeReference(raw).matchAll(/CDV(\d{6})(\d{5})(?!\d)/g)) {
    const chiffres = `${m[1]}${m[2]}`;
    const sept = chiffres.slice(-7);
    if (vus.has(sept)) continue;
    vus.add(sept);
    refs.push({
      digits: sept,
      tag: 'CDV',
      // La forme entière est aussi sûre que `CDV-<TAG>-<AASEQ>` : onze chiffres
      // après `CDV` ne se rencontrent pas par hasard.
      tolerated: false,
      numeroComplet: `CDV-${m[1]}-${m[2]}`,
    });
  }

  // Passe 1 : la forme complète et sûre, sur le texte normalisé — ce qui
  // rattrape `CDV- WBK-2601962`, `cdv wbk 2601586` et leurs variantes.
  for (const m of normalizeReference(raw).matchAll(/CDV([A-Z]+)(\d{7})/g)) {
    if (vus.has(m[2])) continue;
    vus.add(m[2]);
    refs.push({ digits: m[2], tag: m[1], tolerated: false });
  }

  // Passe 2 : le tag seul, sur le texte BRUT parce que le tiret est exigé.
  //
  // Couvre deux cas que la passe 1 ne voit pas : `NDSC-2601466`, où le parent a
  // laissé tomber le `CDV-`, et `CDW-WBK-2601723`, où il a fauté sur le `V`.
  // Ce second cas était détecté par `parseCommunication` mais **jamais
  // rapproché**, faute d'être relu ici : le paiement finissait en file manuelle
  // alors que sa référence avait été comprise.
  for (const m of raw.matchAll(/\b([A-Z]{2,5})\s*-\s*(\d{7})\b/gi)) {
    const tag = m[1].toUpperCase();
    if (vus.has(m[2]) || TAGS_EXCLUS.has(tag)) continue;
    vus.add(m[2]);
    refs.push({ digits: m[2], tag, tolerated: true });
  }

  return refs;
}

/** Les sept chiffres seuls, forme conservée pour la lisibilité des tests. */
export function allLegacyDigits(raw: string): string[] {
  return allLegacyRefs(raw).map((r) => r.digits);
}

/**
 * La forme d'un numéro au format historique `CDV-AAMMJJ-NNNNN`.
 *
 * Exportée parce que trois lecteurs en base doivent la reconnaître pour
 * choisir entre chercher un SUFFIXE et chercher une ÉGALITÉ. Une seule
 * définition, sinon les trois finiront par diverger.
 */
export const NUMERO_HISTORIQUE = /^CDV-\d{6}-\d{5}$/;

/**
 * Ce qu'il faut chercher en base pour chaque référence citée.
 *
 * Le numéro ENTIER quand la référence est au format historique, les sept
 * chiffres sinon. Les deux ne se cherchent pas de la même façon, et c'est tout
 * le point : `CDV-250530-00010` ne CONTIENT pas ses sept derniers chiffres
 * `3000010`, parce qu'un tiret les coupe. Chercher le suffixe ne rendrait
 * jamais rien.
 */
export function allLegacyLookups(raw: string): string[] {
  return allLegacyRefs(raw).map((r) => r.numeroComplet ?? r.digits);
}

/** Le tag d'un numéro de facture, quand il en porte un. */
export function invoiceTag(invoiceNumber: string): string | undefined {
  const m = normalizeReference(invoiceNumber).match(/^CDV([A-Z]+)\d{7}$/);
  return m ? m[1] : undefined;
}

/** Ce qu'une facture réclame encore : montant, moins notes de crédit, moins déjà reçu. */
function resteDu(f: InvoiceRef): number {
  return Math.max(0, netDu(f) - f.allocatedCents);
}

/** Ce que la facture réclame en tout, notes de crédit déduites. */
function netDu(f: InvoiceRef): number {
  return Math.max(0, f.amountCents - (f.creditNotesCents ?? 0));
}

/**
 * Plan pour un lot de paiements, dans l'ordre donné.
 *
 * Le lot compte, il n'est pas un simple `map`. Deux versements pour une même
 * facture existent dans les relevés réels : `CDV-WBK-2601408` réglée par
 * 40,00 € puis 30,00 €. Le second versement doit voir ce que le premier a déjà
 * couvert, sans quoi le moteur proposerait deux fois le montant entier et
 * l'invariant de la base rejetterait l'écriture.
 *
 * L'index n'est pas modifié : un registre local suit les montants engagés.
 */
export function planBatch(mouvements: CodaMovement[], index: InvoiceIndex): MatchPlan[] {
  const engage = new Map<string, number>();

  const disponible = (f: InvoiceRef) => resteDu(f) - (engage.get(f.id) ?? 0);
  const reserver = (f: InvoiceRef, cents: number) =>
    engage.set(f.id, (engage.get(f.id) ?? 0) + cents);

  return mouvements.map((m) => planOne(m, index, disponible, reserver));
}

/** Plan pour un seul paiement. Utile en test ; `planBatch` en production. */
export function planPayment(mouvement: CodaMovement, index: InvoiceIndex): MatchPlan {
  const engage = new Map<string, number>();
  return planOne(
    mouvement,
    index,
    (f) => resteDu(f) - (engage.get(f.id) ?? 0),
    (f, c) => engage.set(f.id, (engage.get(f.id) ?? 0) + c),
  );
}

function planOne(
  m: CodaMovement,
  index: InvoiceIndex,
  disponible: (f: InvoiceRef) => number,
  reserver: (f: InvoiceRef, cents: number) => void,
): MatchPlan {
  const plan: MatchPlan = {
    externalRef: m.reference,
    amountCents: m.amountCents,
    valueDate: m.valueDate,
    allocations: [],
    leftoverCents: m.amountCents,
    manualReasons: [],
  };

  const c = m.communication;

  /*
    Échelon 0 : ce paiement est-il seulement contemporain de nos factures ?

    La première facture de cette application date du 30 mai 2025. Un virement
    reçu avant ne peut en solder aucune — il n'y avait rien à solder. Le
    chercher quand même, c'est fabriquer un faux rapprochement sur les sept
    chiffres d'une référence qui parle d'autre chose.

    Le contrôle vient donc AVANT tout le reste, même avant la communication
    structurée : aucune référence, si bien formée soit-elle, ne peut renvoyer à
    une facture qui n'existait pas.
  */
  if (avantLaFacturation(m.valueDate)) {
    plan.outOfScope = {
      category: 'avant_la_facturation',
      matched: m.valueDate,
      label: 'Antérieur à la première facture',
    };
    plan.outOfScopeCents = plan.leftoverCents;
    plan.manualReasons.push('out_of_scope_pattern');
    return plan;
  }

  // --- Échelon 1 : communication structurée ---------------------------------
  //
  // La recherche se fait par égalité exacte des 12 chiffres avec la
  // communication stockée sur la facture à son émission. Pas par préfixe : une
  // communication de garderie ne sera simplement pas trouvée, et c'est
  // exactement le comportement voulu. Le préfixe (100 centres de vacances, 200
  // parascolaire, 300 études dirigées) sert la lisibilité humaine sur un
  // relevé, pas la justesse du rapprochement.
  if (c.structuredDigits) {
    const facture = index.byStructured(c.structuredDigits);
    if (facture) {
      affecter(plan, facture, 'structured', disponible, reserver);
      return plan;
    }
    // Communication structurée valide mais inconnue : 71 % des paiements reçus
    // sont dans ce cas. Un autre système de l'association, pas une erreur.
    plan.manualReasons.push('foreign_communication');
    return plan;
  }

  // --- Échelon 2 : format historique ---------------------------------------
  // Toujours balayer la communication BRUTE, jamais `legacyInvoiceRef` : ce
  // champ ne porte que la PREMIERE reference trouvee. Passer par lui rendait
  // impossible le cas des deux factures reglees d'un seul virement, qui est
  // precisement au programme.
  const chiffres = allLegacyRefs(c.raw);
  if (chiffres.length === 0) {
    // Avant de dire « aucune référence », voir si la forme trahit une activité
    // qui n'appartient pas à cette comptabilité. C'est le cas de 28 des 45
    // paiements concernés, dont 18 368 € de facturation partenaires.
    const hors = classifyOutOfScope(c.raw);
    if (hors) {
      plan.outOfScope = hors;
      plan.outOfScopeCents = plan.leftoverCents;
      plan.manualReasons.push('out_of_scope_pattern');
    } else {
      plan.manualReasons.push('no_reference');
    }
    return plan;
  }

  let uneTrouvee = false;
  for (const ref of chiffres) {
    /* Le numéro entier d'abord quand la référence en porte un : c'est exact,
       là où les sept chiffres sont une réduction. */
    const facture = ref.numeroComplet
      ? index.byLegacyNumber(ref.numeroComplet) ?? index.byLegacyDigits(ref.digits)
      : index.byLegacyDigits(ref.digits);
    if (!facture) continue;

    // Une référence dont le préfixe a été rattrapé doit prouver qu'elle désigne
    // bien cette facture : son tag doit être celui du numéro trouvé. C'est ce
    // contrôle, et lui seul, qui autorise à lire `NDSC-2601466` comme une
    // référence — sans lui, n'importe quel `XX-1234567` d'un autre système
    // pourrait tomber sur une facture par la seule coïncidence des chiffres.
    if (ref.tolerated) {
      const tagFacture = invoiceTag(facture.invoiceNumber);
      if (!tagFacture || tagFacture !== ref.tag) continue;
    }

    uneTrouvee = true;
    affecter(plan, facture, 'legacy_reference', disponible, reserver);
    if (plan.leftoverCents === 0) break;
  }

  if (!uneTrouvee) plan.manualReasons.push('unknown_reference');

  /*
    Une communication peut parler de DEUX comptabilités à la fois.

    Cas réel : `CDV-MC-2601234 PSD-25`, un virement qui règle une facture d'ici
    et une inscription au parascolaire. Tout attribuer à la facture la ferait
    passer pour sur-payée ; tout écarter perdrait un paiement légitime. Les
    deux sont faux.

    On coupe donc : la facture reçoit ce qu'elle réclame, et **le reste** part
    vers l'autre comptabilité, avec le motif qui l'a fait reconnaître. C'est la
    règle posée par l'administration le 18 septembre 2026.

    Le contrôle vient après la boucle, quand toutes les factures citées ont été
    servies : un virement qui en couvre deux n'a de reste qu'une fois les deux
    payées.
  */
  if (plan.leftoverCents > 0) {
    const hors = classifyOutOfScope(c.raw);
    if (hors) {
      plan.outOfScope = hors;
      plan.outOfScopeCents = plan.leftoverCents;
      plan.manualReasons.push('out_of_scope_pattern');
      return plan;
    }
  }

  // Le surplus ne se constate qu'ici, une fois toutes les factures citees
  // servies. Le decider dans `affecter` — qui ne voit qu'une facture a la fois —
  // signalait un surplus entre les deux allocations d'un virement qui en couvre
  // deux, alors que le paiement etait finalement affecte en entier.
  if (plan.allocations.length > 0 && plan.leftoverCents > 0) {
    plan.manualReasons.push('surplus');
  }
  return plan;
}

/**
 * Affecte au plan ce que la facture peut recevoir, et pas un centime de plus.
 *
 * Le montant affecté est le minimum entre ce qui reste du paiement et ce que la
 * facture attend encore. Trois refus délibérés :
 *
 * - une **facture annulée** ne reçoit rien automatiquement. Le paiement existe,
 *   l'argent est là, mais ce qu'il faut en faire — rembourser, garder en
 *   provision, appliquer à autre chose — est une décision humaine ;
 * - une **facture déjà couverte** ne reçoit rien : le paiement est un doublon
 *   ou concerne autre chose ;
 * Le **surplus** éventuel n'est pas décidé ici mais à la fin du plan : cette
 * fonction ne voit qu'une facture à la fois, et un virement qui en couvre deux
 * n'a de surplus qu'une fois les deux servies. La règle de gestion veut que le
 * surplus aille en provision, automatiquement s'il y a une dette par ailleurs —
 * mais « par ailleurs » suppose une vue d'ensemble que ce moteur n'a pas. Il le
 * signale, l'écran manuel décide. Aucun cas dans les relevés de juin-juillet.
 */
function affecter(
  plan: MatchPlan,
  facture: InvoiceRef,
  method: ProposedAllocation['method'],
  disponible: (f: InvoiceRef) => number,
  reserver: (f: InvoiceRef, cents: number) => void,
): void {
  if (facture.status === 'cancelled') {
    plan.manualReasons.push('invoice_cancelled');
    return;
  }

  const place = disponible(facture);
  if (place <= 0) {
    plan.manualReasons.push(
      netDu(facture) <= 0 ? 'invoice_credited' : 'invoice_settled',
    );
    return;
  }

  const montant = Math.min(plan.leftoverCents, place);
  if (montant <= 0) return;

  plan.allocations.push({
    invoiceId: facture.id,
    invoiceNumber: facture.invoiceNumber,
    amountCents: montant,
    method,
  });
  reserver(facture, montant);
  plan.leftoverCents -= montant;
}

/** Ce que le moteur a fait d'un lot, pour rendre compte avant d'écrire. */
export interface BatchSummary {
  paiements: number;
  centimesRecus: number;
  entierementAffectes: number;
  partiellementAffectes: number;
  enFileManuelle: number;
  centimesAffectes: number;
  parRaison: Record<ManualReason, number>;
  facturesTouchees: number;
}

export function summarize(plans: MatchPlan[]): BatchSummary {
  const parRaison = {
    no_reference: 0,
    unknown_reference: 0,
    foreign_communication: 0,
    invoice_cancelled: 0,
    invoice_settled: 0,
    invoice_credited: 0,
    surplus: 0,
    out_of_scope_pattern: 0,
  } as Record<ManualReason, number>;

  const factures = new Set<string>();
  let centimesRecus = 0, centimesAffectes = 0;
  let entier = 0, partiel = 0, manuel = 0;

  for (const p of plans) {
    centimesRecus += p.amountCents;
    for (const a of p.allocations) {
      centimesAffectes += a.amountCents;
      factures.add(a.invoiceId);
    }
    // Une raison peut apparaître plusieurs fois dans un plan ; on compte les
    // paiements concernés, pas les occurrences.
    for (const r of new Set(p.manualReasons)) parRaison[r]++;

    if (p.allocations.length === 0) manuel++;
    else if (p.leftoverCents === 0) entier++;
    else partiel++;
  }

  return {
    paiements: plans.length,
    centimesRecus,
    entierementAffectes: entier,
    partiellementAffectes: partiel,
    enFileManuelle: manuel,
    centimesAffectes,
    parRaison,
    facturesTouchees: factures.size,
  };
}

/**
 * Les types de communication structurée qui portent une référence de paiement.
 * Réexporté pour que l'importeur n'ait pas à connaître deux modules.
 */
export { PAYMENT_STRUCTURED_TYPES };
